<template>
  <section v-if="baseProductDetail" class="product-detail pt-4 mx-auto w-10/12 pb-12 text-tdefault-color">
    <div class="grid grid-rows">
      <div
        class="my-2 text-primary-default flex cursor-pointer text-sm md:text-base lg:text-base font-bold"
        @click.prevent="goToCatalog"
      >
        <ArrowLeftIcon class="h-5 w-5 flex-shrink-0 mr-2 text-primary-default" aria-hidden="true" />
        Terug naar overzicht
      </div>
      <div id="step1" class="flex flex-col gap-2">
        <v-product-description
          :title="baseProductDetail.title"
          :base-product-detail="baseProductDetail"
          :product-id="baseProductDetail.productCode"
          :sap-id="baseProductDetail.sapId"
          :short-description="baseProductDetail.shortDescription"
          :product-unit="baseProductDetail.packageUnitPackageLabel"
          @favourite-success="loadDetails"
        />
        <div class="flex flex-row gap-4 mb-2">
          <div class="bg-white rounded-md w-9/12">
            <div class="px-6 py-3 bg-balternative-color rounded-t-md">
              <strong>Productinformatie</strong>
            </div>
            <p @click="getImageDetails($event)" v-html="baseProductDetail.description" class="px-6 py-4" />
          </div>
          <div class="w-3/12" @click.prevent="showProductFullImage(baseProductDetail.imageUrl)">
            <vue-image-zoomer
              img-class="zoomImageWrapper"
              :regular="baseProductDetail.thumbnailImageUrl"
              :zoom="baseProductDetail.imageUrl"
            >
              <img @error="handleImageError" :src="handleImageError" class="zoomImageWrapper cursor-pointer" />
            </vue-image-zoomer>
          </div>
        </div>
        <div class="flex flex-row gap-4 mb-2">
          <div class="bg-white rounded-md w-9/12">
            <div class="px-6 py-3 bg-balternative-color rounded-t-md">
              <strong>Gebruiksadvies</strong>
            </div>
            <p @click="getImageDetails($event)" v-html="baseProductDetail.manual" class="px-6 py-4" />
          </div>
          <div class="w-3/12"></div>
        </div>
        <div
          class="grid grid-rows justify-end mt-2 justify-items-end mb-10"
          v-if="!isAssortmentFlow && shoppingCart.items.length > 0"
        >
          <button
            @click.prevent="continueOrdering()"
            class="flex justify-center py-2 px-2 md:px-4 shadow-sm text-xs md:text-sm lg:text-sm font-medium text-white bg-cta rounded-full hover:bg-primary-default/50 mb-2 fixed bottom-20 z-50"
          >
            <span class="">Bestelling afronden ({{ shoppingCart.items.length }})</span>
          </button>
        </div>
        <generic-modal v-if="showProductImage" :has-close-button="true" class="w-full" @close="closeModal">
          <template #body>
            <img :src="getImageUrl(bigImageUrl)" @error="handleImageError" class="max-h-96 w-full" />
          </template>
        </generic-modal>
      </div>
    </div>
  </section>
</template>

<script>
import { shoppingCart$, activeCustomerId$, getBaseProductDetails, getBaseProductDetailsByProductId } from '@/services';
import VProductDescription from './ProductDescription';
import router from '@/router';
import { BehaviorSubject } from 'rxjs';
import GenericModal from '@/components/shared/GenericModal';
import { ArrowLeftIcon } from '@heroicons/vue/20/solid';
import { computed, ref, onMounted, watch } from 'vue';
import { useStore } from 'vuex';

export default {
  name: 'BaseProductDetail',
  components: {
    ArrowLeftIcon,
    VProductDescription,
    GenericModal
  },
  beforeRouteEnter(to, from, next) {
    next();
  },
  beforeRouteLeave(to, from, next) {
    if (to.name !== 'catalog') {
      this.resetCatalogFormDetails();
    }
    next();
  },
  props: {
    productDetails: {
      type: Array,
      default: () => []
    }
  },
  setup() {
    const store = useStore();
    const shoppingCart = ref(null);
    const baseProductDetail = ref(null);
    const count = ref(1);
    const min = ref(1);
    const max = ref(999);
    const showProductImage = ref(false);
    const bigImageUrl = ref('');

    const cartOpenStatus = computed(() => store.getters['cart/getCartOpenStatus']);
    const isCustomerFlow = computed(() => router.currentRoute.value.meta.flow === 'customer');
    const activeCustomerId = computed(() => {
      return isCustomerFlow.value ? activeCustomerId$ : new BehaviorSubject(null);
    });
    const isReplenishmentFlow = computed(() => router.currentRoute.value.meta.flow === 'replenishment');
    const isAssortmentFlow = computed(() => router.currentRoute.value.meta.flow === 'assortment');

    const resetCatalogFormDetails = () => store.commit('catalog/resetCatalogFormDetails');
    const setCartOpenStatus = (value) => store.commit('cart/setCartOpenStatus', value);

    shoppingCart$.subscribe((value) => {
      shoppingCart.value = value;
    });

    const loadDetails = () => {
      if (router.currentRoute.value.name === 'aanvullenmagazijnProductDetail') {
        getBaseProductDetailsByProductId(router.currentRoute.value.params.id, isCustomerFlow.value, true).then((r) => {
          baseProductDetail.value = r;
          gtmDataForViewProduct();
        });
      } else if (router.currentRoute.value.name === 'productDetail') {
        getBaseProductDetailsByProductId(router.currentRoute.value.params.id, isCustomerFlow.value, false).then((r) => {
          baseProductDetail.value = r;
          gtmDataForViewProduct();
        });
      } else {
        getBaseProductDetails(router.currentRoute.value.params.id, isCustomerFlow.value).then((r) => {
          baseProductDetail.value = r;
          gtmDataForViewProduct();
        });
      }
    };

    const gtmDataForViewProduct = () => {
      let products = [];
      if (baseProductDetail.value && baseProductDetail.value.products.length > 0) {
        baseProductDetail.value.products.forEach((product) => {
          products.push({
            item_id: product.productId,
            item_name: product.title,
            item_brand: baseProductDetail.value.manufName,
            quantity: 1,
            item_relationship: product.productRelationType ? product.productRelationType : ''
          });
        });
        window.dataLayer.push({
          event: 'view_item_list',
          ecommerce: {
            item_list_name: router.currentRoute.value.path,
            items: products
          }
        });
      }
    };

    const getEnumValue = (enumList, enumKey) => {
      for (const key in enumList) {
        if (enumList.hasOwnProperty(key)) {
          if (key === enumKey) {
            return enumList[key];
          }
        }
      }
    };

    const restrictCount = (count) => {
      let regex = /^([0-9]+)$/;
      if (!regex.test(count) || !count || count <= 0) {
        this.count = this.min;
      }
    };

    const goToCatalog = () => {
      if (isCustomerFlow.value) {
        router.push('/catalog');
      } else if (isReplenishmentFlow.value) {
        router.push('/aanvullenmagazijn');
      } else if (isAssortmentFlow.value) {
        router.push('/assortiment');
      }
    };

    const continueOrdering = () => {
      if (isReplenishmentFlow.value) {
        router.push('/magazijnaanvullenBestellijst');
      } else {
        router.push('/bestellijst');
      }
    };

    const goToBaseProductDetailPage = (baseProductId) => {
      if (isReplenishmentFlow.value) {
        router.push('/aanvullenmagazijn/baseProduct/' + baseProductId);
      } else {
        router.push('/catalog/baseProduct/' + baseProductId);
      }
    };

    const closeModal = () => {
      showProductImage.value = false;
    };

    const showProductFullImage = (imageUrl) => {
      bigImageUrl.value = imageUrl;
      showProductImage.value = true;
    };

    const getImageDetails = (event) => {
      if (event.target.tagName === 'IMG') {
        showProductFullImage(event.target.getAttribute('src').replace('afbeeldingenN', 'afbeeldingenM'));
      }
    };

    onMounted(() => {
      loadDetails();
    });

    watch(
      () => router.currentRoute.value.params.id,
      (id) => {
        if (id) {
          loadDetails();
        }
      }
    );

    return {
      baseProductDetail,
      count,
      min,
      max,
      showProductImage,
      bigImageUrl,
      isReplenishmentFlow,
      isAssortmentFlow,
      cartOpenStatus,
      resetCatalogFormDetails,
      setCartOpenStatus,
      shoppingCart,
      activeCustomerId,
      isCustomerFlow,
      loadDetails,
      gtmDataForViewProduct,
      getEnumValue,
      restrictCount,
      goToCatalog,
      continueOrdering,
      goToBaseProductDetailPage,
      closeModal,
      showProductFullImage,
      getImageDetails
    };
  }
};
</script>
